import React from 'react';
import { Alert, Box, Button, LinearProgress, Stack, Typography } from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useStudentActiveAssessmentResume } from '../../hooks/queries/studentAssessmentQueries';
import { studentAssessmentLivePath } from '../../constants/studentAssessments';
import { LIFECYCLE_CTA } from '../../utils/assessmentLifecyclePresentation';

/**
 * Global resume affordance when a student leaves the live workspace (e.g. after
 * guided return focuses the container tab on Home/Dashboard). Guided return only
 * focuses the browser tab — it does not navigate back to /live.
 */
export default function StudentAssessmentResumeBar() {
  const location = useLocation();
  const { isEnabled, inProgress, item, progress, isLoading } = useStudentActiveAssessmentResume();

  if (!isEnabled || isLoading || !inProgress || !item?.assessmentId) {
    return null;
  }

  const livePath = studentAssessmentLivePath(item.assessmentId);
  if (location.pathname === livePath || location.pathname.endsWith('/live')) {
    return null;
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Alert
        severity="info"
        variant="outlined"
        data-assessment-resume-bar
        action={
          <Button
            color="inherit"
            size="small"
            component={RouterLink}
            to={livePath}
            data-assessment-resume-continue
            sx={{ whiteSpace: 'nowrap' }}
          >
            {LIFECYCLE_CTA.CONTINUE}
          </Button>
        }
      >
        <Stack spacing={0.75}>
          <Typography variant="subtitle2">
            {item.title || 'Assessment'} — in progress
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your guided coding tab may still be open. Return to the assessment workspace to
            select a problem or reopen the managed tab.
          </Typography>
          {progress && progress.totalProblems > 0 ? (
            <Box sx={{ maxWidth: (theme) => theme.spacing(45) }}>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
                <Typography variant="caption" color="text.secondary">
                  {progress.solvedCount} of {progress.totalProblems} solved
                </Typography>
                <Typography variant="caption" color="primary.main">
                  {Math.round(progress.percentage)}%
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={progress.percentage}
                sx={{ height: (theme) => theme.spacing(0.75), borderRadius: 99 }}
              />
            </Box>
          ) : null}
        </Stack>
      </Alert>
    </Box>
  );
}
